import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { addMonths, format, isSameDay } from "date-fns";
import { api, extractError } from "@/lib/api";
import { DateSelector } from "@/components/timemachine/DateSelector";
import { DailyReports, fmtDay } from "@/components/timemachine/DailyReports";
import { WeeklyConditions } from "@/components/timemachine/WeeklyConditions";
import { EventsList } from "@/components/timemachine/EventsList";
import { AISummary } from "@/components/timemachine/AISummary";
import { DailyPlansDigest } from "@/components/timemachine/DailyPlansDigest";

const iso = (d) => format(d, "yyyy-MM-dd");
const romeToday = () => parseDay(new Date().toLocaleDateString("en-CA", { timeZone: "Europe/Rome" }));
const parseDay = (s) => (s && /^\d{4}-\d{2}-\d{2}$/.test(s) ? new Date(`${s}T00:00:00`) : null);

function ReportsByDay({ reports, onChange }) {
    const days = [...new Set(reports.map((r) => r.date))];
    if (days.length === 0) return <div className="text-sm text-neutral-500 border border-dashed border-neutral-300 rounded-md p-6 bg-white" data-testid="tm-range-empty">Nessun rapporto giornaliero in questo intervallo. Seleziona un singolo giorno per scriverne uno.</div>;
    return days.map((d) => (
        <div key={d} className="space-y-2">
            <div className="text-xs uppercase tracking-wider text-neutral-500 font-semibold capitalize">{fmtDay(d)}</div>
            <DailyReports reports={reports.filter((r) => r.date === d)} onChange={onChange} />
        </div>
    ));
}

export default function TimeMachine() {
    const [params, setParams] = useSearchParams();
    const initFrom = parseDay(params.get("d")) || romeToday();
    const initTo = parseDay(params.get("to")) || initFrom;
    const [mode, setMode] = useState(isSameDay(initFrom, initTo) ? "single" : "range");
    const [range, setRange] = useState({ from: initFrom, to: initTo });
    const [month, setMonth] = useState(initFrom);
    const [reports, setReports] = useState([]);
    const [markedDays, setMarkedDays] = useState([]);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    const start = iso(range.from);
    const end = iso(range.to || range.from);
    const urlD = params.get("d");
    const urlTo = params.get("to");
    const single = mode === "single" || start === end;

    const loadReports = useCallback(async (signal) => {
        try {
            const { data } = await api.get("/timemachine/reports", { params: { start, end }, signal });
            setReports(data);
        } catch (e) { if (!signal?.aborted) setError(extractError(e)); }
    }, [start, end]);

    const loadData = useCallback(async (signal) => {
        setData(null);
        setError(null);
        try {
            const { data: d } = await api.get("/timemachine/data", { params: { start, end }, signal });
            setData(d);
        } catch (e) { if (!signal?.aborted) setError(extractError(e)); }
    }, [start, end]);

    const loadMarked = useCallback(async () => {
        try {
            const { data } = await api.get("/timemachine/marked-days", {
                params: { start: iso(addMonths(month, -1)), end: iso(addMonths(month, 2)) },
            });
            setMarkedDays(data.days);
        } catch (e) { console.error("Load marked days failed:", e); }
    }, [month]);

    // URL -> state (e.g. a global-search link while already on this page)
    useEffect(() => {
        const f = parseDay(urlD);
        if (!f) return;
        const t = parseDay(urlTo) || f;
        if (iso(f) === start && iso(t) === end) return;
        setMode(isSameDay(f, t) ? "single" : "range");
        setRange({ from: f, to: t });
        setMonth(f);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlD, urlTo]);
    useEffect(() => {
        const ctrl = new AbortController();
        loadReports(ctrl.signal);
        loadData(ctrl.signal);
        return () => ctrl.abort();
    }, [loadReports, loadData]);
    // state -> URL
    useEffect(() => {
        setParams(single ? { d: start } : { d: start, to: end }, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [single, start, end]);
    useEffect(() => { loadMarked(); }, [loadMarked]);

    const refresh = () => { loadReports(); loadMarked(); };

    return (
        <div className="p-6 space-y-6 max-w-6xl" data-testid="time-machine-page">
            <div>
                <h1 className="font-display text-2xl font-semibold tracking-tight text-neutral-900">Time Machine</h1>
                <div className="text-sm text-neutral-500 mt-1 capitalize" data-testid="tm-selected-label">
                    {single ? fmtDay(start) : `${fmtDay(start)} → ${fmtDay(end)}`}
                </div>
            </div>
            <DateSelector mode={mode} setMode={setMode} range={range} setRange={setRange}
                markedDays={markedDays} month={month} setMonth={setMonth} />
            {!(mode === "range" && !range.to) && <AISummary start={start} end={end} />}
            {data && (
                <section className="space-y-3">
                    <div className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold">Piani settimanali</div>
                    <WeeklyConditions weeks={data.weeks} />
                </section>
            )}
            <section className="space-y-3" data-testid="tm-reports-section">
                <div className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold">Rapporti giornalieri</div>
                {single
                    ? <DailyReports day={start} reports={reports} onChange={refresh}
                        emptyHint={data && data.events.length === 0
                            ? "Per questo giorno non risulta nulla di registrato. Puoi comunque scrivere un rapporto a posteriori."
                            : "Per questo giorno non risulta ancora nessun rapporto. Puoi scriverne uno anche a posteriori."} />
                    : <ReportsByDay reports={reports} onChange={refresh} />}
            </section>
            {data && (
                <section className="space-y-3" data-testid="tm-plans-section">
                    <div className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold">Piani giornalieri</div>
                    <DailyPlansDigest plans={data.daily_plans} single={single} />
                </section>
            )}
            <section className="space-y-3" data-testid="tm-events-section">
                <div className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold">Eventi registrati</div>
                {error ? <div className="text-sm text-red-600" data-testid="tm-error">Errore nel caricamento: {error}</div>
                    : !data ? <div className="text-sm text-neutral-400">Caricamento…</div>
                    : data.events.length === 0
                        ? <div className="text-sm text-neutral-500 border border-dashed border-neutral-300 rounded-md p-6 bg-white" data-testid="tm-no-events">Nessun evento registrato nel gestionale per {single ? "questo giorno" : "questo intervallo"}.</div>
                        : <EventsList events={data.events} />}
                {data && Object.entries(data.source_errors || {}).map(([k, v]) => (
                    <div key={k} className="text-xs text-red-600" data-testid={`tm-source-error-${k}`}>{({ google_calendar: "Google Calendar", gmail: "Gmail", notion: "Notion" })[k] || k}: {v}</div>
                ))}
            </section>
        </div>
    );
}
